import { useEffect, useRef, useCallback } from 'react';
import {
  clearToken,
  getRefreshToken,
  getSessionStart,
  setToken,
} from '@/redux/slice/tokenSlice';
import { useDispatch, useSelector } from 'react-redux';
import { Axios } from '@/components/base-axios';
import { useIdleTimer } from 'react-idle-timer';
import { useNavigate } from 'react-router-dom';
import { ABSOLUTE_MS, IDLE_MS } from '@/Config';

export const useSessionTimeout = (): void => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const refreshToken = useSelector(getRefreshToken);
  const sessionStartValue = useSelector(getSessionStart);
  const absoluteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Refresh 1 minute before the access token expires
  const REFRESH_INTERVAL_MS = IDLE_MS - 60 * 1000;

  const clearAllTimers = useCallback((): void => {
    if (absoluteTimerRef.current) clearTimeout(absoluteTimerRef.current);
    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
  }, []);

  const logout = useCallback((): void => {
    clearAllTimers();
    dispatch(clearToken());
    navigate('/login', { replace: true });
  }, [clearAllTimers]);

  // Silent refresh token before the access token expires
  const silentRefresh = useCallback(async (): Promise<void> => {
    try {
      const response = await Axios.post('/auth/refresh-token', {
        refresh_token: refreshToken,
      });
      if (response?.data?.access_token) {
        dispatch(
          setToken({
            token: response?.data?.access_token,
            refreshToken: response?.data?.refresh_token,
          })
        );
      }
    } catch {
      logout();
    }
  }, [logout]);

  useEffect(() => {
    // Calculate remaining absolute session time from the original sessionStart
    const sessionStart = sessionStartValue || 0;
    const elapsedMs = Date.now() - sessionStart * 1000;
    const remainingAbsoluteMs = ABSOLUTE_MS - elapsedMs;

    // If session is already over, log out immediately
    if (remainingAbsoluteMs <= 0) {
      logout();
      return;
    }

    // Set the absolute ceiling timer
    absoluteTimerRef.current = setTimeout(() => logout(), remainingAbsoluteMs);

    // Schedule silent token refresh every 14 minutes
    refreshTimerRef.current = setInterval(silentRefresh, REFRESH_INTERVAL_MS);

    return () => {
      clearAllTimers();
    };
  }, [logout, silentRefresh, clearAllTimers]);

  // Reset idle timer on any user activity
  useIdleTimer({
    timeout: IDLE_MS, // 15 minutes
    onIdle: () => {
      dispatch(clearToken());
      navigate('/login', { replace: true });
    },
    debounce: 500,
  });
};
